import { Injectable, inject } from '@angular/core';
import {
  DetectionService,
  RenderService,
  SimulationService,
  TaskService,
} from '@services';
import '@tensorflow/tfjs-backend-webgl';
import { SharedService } from './shared.service';
import { StratafixMetricsService } from './stratafix-metrics.service';

const WORKERS = {
  cycleWorker: {
    id: 'cycleWorker',
    model: 'default',
    historySize: 20,
    confidence: 0.2,
  },
  piercingBackStitchWorker: {
    id: 'piercingBackStitchWorker',
    model: 'piercingBackStitchTensioner',
    historySize: 20,
    confidence: 0.2,
  },
};

@Injectable({
  providedIn: 'root',
})
export class StratafixRightService {
  private detectionService = inject(DetectionService);
  private stratafixMetricsService = inject(StratafixMetricsService);
  private taskService = inject(TaskService);
  private renderService = inject(RenderService);
  private shared = inject(SharedService);
  private simulationService = inject(SimulationService);

  constructor() {
    this.detectionService.addWorkers(WORKERS);
    this.shared.cycleWorkerId = WORKERS.cycleWorker.id;
    this.shared.piercingBackStitchWorkerId =
      WORKERS.piercingBackStitchWorker.id;
  }

  async runDetection() {
    if (this.simulationService.isSimulationRunning()) {
      await this.detectionService.detect(WORKERS.cycleWorker.id);
      if (this.simulationService.state() === 'kit') {
        this.shared.checkSample();
      } else if (this.simulationService.state() === 'simulation') {
        this.shared.toolTracking();
        switch (this.taskService.currentTaskId()) {
          case 'checkSample': {
            this.shared.checkSample().then(async (isConfirmed) => {
              if (isConfirmed) {
                await this.renderService.waitForStaticCleanup();
                await this.taskService.completeTask('checkSample');
              }
            });
            break;
          }
          case 'checkInstruments': {
            this.shared.checkInstruments().then(async (isConfirmed) => {
              if (isConfirmed) {
                await this.renderService.waitForStaticCleanup();
                await this.taskService.completeTask('checkInstruments');
              }
            });
            break;
          }
          case 'fixation': {
            await this.detectionService.detect(
              WORKERS.piercingBackStitchWorker.id,
            );
            this.shared.fixation().then(async (isConfirmed) => {
              if (isConfirmed) {
                this.renderService.clearDynamicOverlays();
                await this.renderService.waitForStaticCleanup();
                await this.taskService.completeTask('fixation');
              }
            });
            break;
          }
          case 'lockStitch': {
            await this.detectionService.detect(
              WORKERS.piercingBackStitchWorker.id,
            );
            this.shared.lockStitch().then(async (isConfirmed) => {
              if (isConfirmed) {
                await this.taskService.completeTask('lockStitch');
              }
            });
            break;
          }
          case 'closeWound': {
            await this.detectionService.detect(
              WORKERS.piercingBackStitchWorker.id,
            );
            this.shared.closeWound().then(async (isConfirmed) => {
              if (isConfirmed) {
                await this.taskService.completeTask('closeWound');
              }
            });
            break;
          }
          case 'reverseStitches': {
            await this.detectionService.detect(
              WORKERS.piercingBackStitchWorker.id,
            );
            this.shared.reverseStitches().then(async (isConfirmed) => {
              if (isConfirmed) {
                await this.taskService.completeTask('reverseStitches');
              }
            });
            break;
          }
          case 'cutSuture': {
            if (!this.taskService.taskListCompleted()) {
              this.shared.cutSuture().then(async (isConfirmed) => {
                if (isConfirmed) {
                  await this.taskService.completeTask('cutSuture');
                }
              });
            }
            break;
          }
        }
      }
    }
  }

  stopDetection() {
    this.stratafixMetricsService.finalizeMetrics();
    this.detectionService.terminateAllworkers();
  }
}
